import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type UserRow } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { timeAgo } from "../../lib/format";
import {
  Avatar,
  Card,
  ErrorNote,
  PageHeader,
  Spinner,
  useToast,
} from "../../components/ui";

// Role pill colours, from the prototype.
const ROLE_BG: Record<string, string> = {
  ADMIN: "#E4F5FB",
  NCM: "#E6F7DC",
  NCE: "#F3EEFA",
  SYSTEM_ENGINEER: "#EFECEE",
};
const ROLE_FG: Record<string, string> = {
  ADMIN: "#1D5F72",
  NCM: "#2F6420",
  NCE: "#4B2E83",
  SYSTEM_ENGINEER: "#57515B",
};

const ROLES = [
  { value: "ADMIN", label: "Admin" },
  { value: "NCM", label: "NCM" },
  { value: "NCE", label: "NCE" },
  { value: "SYSTEM_ENGINEER", label: "System Engineer" },
];

type MatrixRow = {
  capability: string;
  key: string;
  roles: Record<string, boolean>;
};

export function UsersScreen() {
  const me = useUser();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("NCE");

  const { data: users, isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<UserRow[]>("/admin/users"),
  });

  const { data: matrix } = useQuery({
    queryKey: ["matrix"],
    queryFn: async () =>
      (await api.get<{ matrix: MatrixRow[] }>("/admin/matrix")).matrix,
    staleTime: Infinity,
  });

  const { data: groups } = useQuery({
    queryKey: ["entra-groups"],
    queryFn: () =>
      api.get<{ name: string; role: string; members: number | null }[]>(
        "/admin/entra-groups",
      ),
  });

  const { data: requests } = useQuery({
    queryKey: ["access-requests"],
    queryFn: () =>
      api.get<
        {
          id: string;
          displayName: string;
          email: string;
          requestedRole: string;
          detail: string;
        }[]
      >("/admin/access-requests"),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<UserRow>("/admin/users", {
        email: inviteEmail.trim(),
        role: inviteRole,
      }),
    onSuccess: (user) => {
      toast(`${user.email} invited as ${user.roleLabel}`);
      setInviteOpen(false);
      setInviteEmail("");
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Could not invite", "danger"),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch<UserRow>(`/admin/users/${id}/role`, { role }),
    onSuccess: (user) => {
      toast(`${user.displayName} is now ${user.roleLabel}`);
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Could not change role", "danger"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/admin/users/${id}`),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Could not revoke", "danger"),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api.post<{ message: string }>(`/admin/access-requests/${id}/${decision}`),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["access-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Access"
        title="Users & roles"
        subtitle="In production, access is inherited from Entra ID groups and refined per category"
        actions={
          <button className="btn-primary" onClick={() => setInviteOpen((v) => !v)}>
            {inviteOpen ? "Cancel" : "Invite user"}
          </button>
        }
      />

      {inviteOpen && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-[240px] flex-1">
              <span className="eyebrow mb-1.5 block">Work email</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="name@sgn.co.uk"
                className="field"
              />
            </label>
            <label>
              <span className="eyebrow mb-1.5 block">Role</span>
              <select
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
                className="field"
              >
                {ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn-accent"
              disabled={!inviteEmail.trim() || invite.isPending}
              onClick={() => invite.mutate()}
            >
              Send invitation
            </button>
          </div>
          <p className="mt-2 text-2xs text-muted-soft">
            No password is set. Under Entra ID the account never needs one; under
            local sign-in an administrator issues one separately.
          </p>
        </Card>
      )}

      <Card className="mb-4" padded={false}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead>
              <tr className="table-head">
                <th className="px-4 py-2.5">User</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Categories</th>
                <th className="px-3 py-2.5">Last active</th>
                <th className="px-3 py-2.5">Source</th>
                <th className="w-16 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-line-soft last:border-0 hover:bg-surface-hover"
                >
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2.5">
                      <Avatar
                        initials={user.initials}
                        colour={user.avatarColour}
                        size={28}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-base font-bold text-ink">
                          {user.displayName}
                          {user.id === me.id && (
                            <span className="ml-1.5 text-2xs font-normal text-muted-soft">
                              (you)
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-2xs text-muted-soft">
                          {user.email}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <select
                      value={user.role}
                      disabled={changeRole.isPending}
                      onChange={(event) =>
                        changeRole.mutate({ id: user.id, role: event.target.value })
                      }
                      className="rounded-pill border bg-surface px-[7px] py-[4px] text-sm font-extrabold outline-none"
                      style={{
                        borderColor: ROLE_FG[user.role] ?? "#57515B",
                        background: ROLE_BG[user.role] ?? "#EFECEE",
                        color: ROLE_FG[user.role] ?? "#57515B",
                      }}
                      aria-label={`Role for ${user.displayName}`}
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-strong">
                    {user.scopeLabel}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-strong">
                    {timeAgo(user.lastActiveAt)}
                  </td>
                  <td className="px-3 py-3 text-2xs text-muted-soft">
                    {user.sourceGroup ?? "Local"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {user.id !== me.id && (
                      <button
                        className="btn-quiet !px-2 !py-1 !text-2xs"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke access for ${user.displayName}? Their history is retained so the audit log stays readable.`,
                            )
                          )
                            revoke.mutate(user.id);
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Permission matrix" collapsible storageKey="permission-matrix" defaultOpen={false} padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left">
              <thead>
                <tr className="table-head">
                  <th className="px-4 py-2.5">Capability</th>
                  {ROLES.map((role) => (
                    <th key={role.value} className="px-2 py-2.5 text-center">
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix?.map((row) => (
                  <tr key={row.key} className="border-b border-line-soft last:border-0">
                    <td className="px-4 py-2 text-sm text-muted-strong">
                      {row.capability}
                    </td>
                    {ROLES.map((role) => {
                      const allowed = row.roles[role.value];
                      return (
                        <td key={role.value} className="px-1 py-1">
                          <span
                            className="block rounded-[6px] px-[10px] py-2 text-center text-base+ font-extrabold"
                            style={
                              allowed
                                ? { background: "#E9F8E0", color: "#2F6420" }
                                : { background: "#FAF8F9", color: "#CFC8CD" }
                            }
                            aria-label={allowed ? "allowed" : "not allowed"}
                          >
                            {allowed ? "✓" : "—"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="Entra ID groups" collapsible storageKey="entra-groups" defaultOpen={false}>
            <ul className="space-y-2">
              {groups?.map((group) => (
                <li
                  key={group.name}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-bold text-ink">
                      {group.name}
                    </span>
                    <span className="text-2xs text-muted-soft">
                      maps to {group.role}
                    </span>
                  </span>
                  <span className="text-2xs font-bold text-muted">
                    {group.members === null ? "—" : `${group.members} members`}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 border-t border-line-soft pt-2.5 text-2xs leading-relaxed text-muted-soft">
              Member counts are unavailable while the directory is not connected.
              New starters default to System Engineer, which is read-only, and
              every role change is written to the audit log.
            </p>
          </Card>

          {(requests?.length ?? 0) > 0 && (
            <Card title="Pending access requests" padded={false}>
              <ul>
                {requests?.map((request) => (
                  <li
                    key={request.id}
                    className="flex flex-wrap items-center gap-2 border-b border-line-soft px-4 py-3 last:border-0"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-bold text-ink">
                        {request.displayName}
                      </span>
                      <span className="block text-2xs text-muted-soft">
                        {request.detail || request.email} · wants{" "}
                        {request.requestedRole}
                      </span>
                    </span>
                    <button
                      className="btn-accent !py-1"
                      onClick={() =>
                        decide.mutate({ id: request.id, decision: "grant" })
                      }
                    >
                      Grant
                    </button>
                    <button
                      className="btn-ghost !py-1"
                      onClick={() =>
                        decide.mutate({ id: request.id, decision: "decline" })
                      }
                    >
                      Decline
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
